"use client";
import { ReactNode, useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils/utils";

interface VelocityMarqueeProps {
  children: ReactNode;
  /** Idle travel in px per frame, before any scroll boost. */
  speed?: number;
  /** 1 travels left, -1 travels right. */
  direction?: 1 | -1;
  /** Degrees of shear per px/frame of scroll velocity. 0 turns the lean off. */
  shear?: number;
  className?: string;
  trackClassName?: string;
}

// A ribbon that reads the page's scroll. It always drifts, speeds up while the
// reader scrolls, reverses when they scroll back, and leans into the movement.
//
// TextMarquee animates a fixed keyframe loop, which cannot react to input; this
// one drives the transform itself, so the whole thing lives in a rAF loop and
// the group is rendered exactly twice — the shift wraps at one group's width.
const VelocityMarquee = ({
  children,
  speed = 0.5,
  direction = 1,
  shear = 0.012,
  className,
  trackClassName,
}: VelocityMarqueeProps) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const host = hostRef.current;
    const track = trackRef.current;
    if (!host || !track || reduceMotion) return;

    // Half the track is one group. Re-measured on resize and on the webfont
    // swap, both of which change the width the loop wraps at.
    let width = 0;
    const sizeObserver = new ResizeObserver(() => {
      width = track.scrollWidth / 2;
    });
    sizeObserver.observe(track);

    let offset = 0;
    let lastY = window.scrollY;
    let velocity = 0;
    let frame = 0;

    const tick = () => {
      const y = window.scrollY;
      // Lerped toward the frame's scroll delta, so the boost eases away after
      // the wheel stops rather than snapping back to idle on the next frame.
      velocity += (y - lastY - velocity) * 0.12;
      lastY = y;

      if (width) {
        const boost = Math.min(Math.abs(velocity), 90) * 0.09;
        const sign = velocity < 0 ? -1 : 1;
        offset -= (speed + boost) * direction * sign;
        if (offset <= -width) offset += width;
        if (offset >= 0) offset -= width;
        track.style.transform = `translate3d(${offset.toFixed(2)}px,0,0) skewX(${(velocity * -shear).toFixed(3)}deg)`;
      }

      frame = requestAnimationFrame(tick);
    };

    // A page can carry several of these. Off-screen ones and a backgrounded tab
    // must not keep a rAF loop and a compositor layer alive for nothing.
    const start = () => {
      if (frame || document.hidden) return;
      lastY = window.scrollY;
      frame = requestAnimationFrame(tick);
    };
    const stop = () => {
      if (!frame) return;
      cancelAnimationFrame(frame);
      frame = 0;
    };

    let onScreen = false;
    const viewObserver = new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      if (onScreen) start();
      else stop();
    });
    viewObserver.observe(host);

    const onVisibility = () =>
      document.hidden || !onScreen ? stop() : start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stop();
      sizeObserver.disconnect();
      viewObserver.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [speed, direction, shear, reduceMotion]);

  return (
    <div ref={hostRef} className={cn("overflow-hidden", className)}>
      <div
        ref={trackRef}
        className={cn("flex w-max will-change-transform", trackClassName)}
      >
        <div className="flex shrink-0">{children}</div>
        {/* Second copy is the seam filler, never announced twice. */}
        <div className="flex shrink-0" aria-hidden>
          {children}
        </div>
      </div>
    </div>
  );
};

export default VelocityMarquee;
